import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Divider } from "@mui/material";
import { DrawerContent, DrawerFooter, Flex } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect } from "@zfp-akademie-a-s/zfp-design/form";
import { useGoalsFulfillmentContext } from "Components/Analyses/GoalsFulfillment/GoalsFulfillmentContext";
import {
  type ProposedSolutionWrapperFormType,
  getProposedSolutionWrapperFormSchema,
} from "Forms/Schemas/GoalsFulfillment/ProposedSolution/proposedSolutionWrapperFormSchema";
import { toSelectOptions } from "Forms/fieldOptions";
import { ContractProposalBaseInvestmentToolType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { InvestmentsNsureCaseStatus } from "./InvestmentsNsureCaseStatus";
import { InvestmentsNsureEvaluationPanel } from "./InvestmentsNsureEvaluationPanel";
import { NsureSelectionContext } from "./InvestmentsNsureSelectionContext";
import { InvestmentsNsureSolutionSelect } from "./InvestmentsNsureSolutionSelect";

type Props = {
  readonly onFormTypeChange: (type: ContractProposalBaseInvestmentToolType) => void;
  readonly isEditMode: boolean;
  readonly onClose: () => void;
  readonly onContinue: () => void;
  readonly onDelete?: () => void;
};

export const InvestmentsNsureSelectStep = ({ onFormTypeChange, isEditMode, onContinue, onClose, onDelete }: Props) => {
  const { t } = useResource();
  const { selection, evaluation } = useContext(NsureSelectionContext);
  const { isInvestmentCaseLocked, lockedCaseStatus } = useGoalsFulfillmentContext();

  const { control, setValue } = useForm<ProposedSolutionWrapperFormType>({
    resolver: yupResolver(getProposedSolutionWrapperFormSchema(t)),
    mode: "onChange",
    defaultValues: {
      formType: ContractProposalBaseInvestmentToolType.investment,
    },
  });

  const formTypeOptions = (
    Object.keys(ContractProposalBaseInvestmentToolType) as Array<keyof typeof ContractProposalBaseInvestmentToolType>
  ).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions[value]),
  }));

  return (
    <>
      <DrawerContent>
        <Flex flexDirection="column">
          <form>
            <Box maxWidth={380}>
              <FormSelect
                control={control}
                name="formType"
                label={t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions.Title)}
                required
                options={toSelectOptions(formTypeOptions)}
                placeholder={t(Resources.Common.SelectPlaceholder)}
                disabled={isEditMode}
                onChange={(value) => {
                  setValue("formType", value as ContractProposalBaseInvestmentToolType);
                  onFormTypeChange(value as ContractProposalBaseInvestmentToolType);
                }}
                fullWidth
              />
            </Box>
          </form>
          <Divider sx={{ mt: Spacing.XL + Spacing.S }} />
          {isInvestmentCaseLocked ? (
            <Flex py={Spacing.XXL} justifyContent={"center"}>
              <InvestmentsNsureCaseStatus caseStatus={lockedCaseStatus} />
            </Flex>
          ) : (
            // the list and the panel side by side, each under its title, the panel's line between them
            <Flex sx={{ flexDirection: { xs: "column", lg: "row" } }} gap={Spacing.XXXL} pt={Spacing.XL + Spacing.S} pb={Spacing.XXL}>
              <InvestmentsNsureSolutionSelect />
              <InvestmentsNsureEvaluationPanel />
            </Flex>
          )}
        </Flex>
      </DrawerContent>
      <DrawerFooter
        startAction={
          onDelete && (
            <Button variant="text" color="error" onClick={onDelete}>
              {t(Resources.Common.Delete)}
            </Button>
          )
        }
        onClose={onClose}
        closeLabel={t(Resources.Common.Close)}
        primaryAction={
          <Button
            variant="contained"
            onClick={onContinue}
            disabled={
              isInvestmentCaseLocked ||
              !selection ||
              evaluation.hasWarning ||
              evaluation.isPending ||
              evaluation.isDataStale ||
              evaluation.isError
            }
          >
            {t(Resources.Common.Continue)}
          </Button>
        }
      />
    </>
  );
};
